import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

function Doc({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="page">
      <Link to="/" className="wordmark" style={{ textDecoration: 'none' }}>Lineage</Link>
      <h1 style={{ margin: '16px 0' }}>{title}</h1>
      <div className="stack">{children}</div>
    </div>
  );
}

/** Date shown on the legal pages; matches `TERMS_VERSION` in packages/core. */
const EFFECTIVE = 'November 4, 2026';

export function Privacy() {
  return (
    <Doc title="Privacy policy">
      <p className="muted">Effective {EFFECTIVE}</p>
      <p>Lineage is a private family-tree app run by Vir Ventures Inc, a company in Ontario, Canada (“we”). Every tree is visible only to the people its owner has invited. There are no ads, and we never sell your data or share it for advertising. This policy explains what we collect, why, and the choices you have.</p>

      <h2>What we store to run Lineage</h2>
      <ul>
        <li><strong>Your account:</strong> your email address (if your sign-in has one), your name, how you signed in, your language and display choices, the country you signed up from, and when you agreed to these terms.</li>
        <li><strong>Family trees:</strong> the people, relationships, dates, places, contact details, photos and stories you or your relatives add, and a history of edits so changes can be undone.</li>
      </ul>
      <p>We use this only to provide Lineage to you and the people you share a tree with.</p>

      <h2>Usage statistics (analytics)</h2>
      <p>To learn which features help families and to fix problems, we record how Lineage is used. For example: a page was opened, an account was created, a tree was created, a person or photo was added, an invite was sent, opened or accepted, or a request failed. We use these to count things like new trees and members per day, week and month, and visitors by country.</p>
      <ul>
        <li>We use <strong>PostHog</strong> (PostHog Inc, servers in the United States) for these statistics. When you’re signed in, events are linked to a random account number, never your email address or name.</li>
        <li>We never send names, email addresses, photos, stories or anything you type in a tree. Invite links and tree addresses are removed from page addresses before they’re sent.</li>
        <li>We record your country, your device type and browser, and the page that sent you to us. We don’t record screen recordings, keystrokes or your precise location.</li>
        <li>PostHog stores an identifier in your browser’s or app’s local storage so visits can be counted. It does not follow you across other websites or apps.</li>
        <li>We also keep anonymous counts in Cloudflare (Workers Analytics Engine and Web Analytics), which hold no account numbers or cookies.</li>
        <li>Usage statistics are kept for up to 12 months.</li>
      </ul>
      <p><strong>Your choice.</strong> In the European Union, the wider European Economic Area, the United Kingdom and Switzerland, we ask before turning analytics on and nothing is sent unless you choose Allow. Everywhere else analytics is on by default. Wherever you are, you can turn it off at any time in <strong>Settings → Share usage statistics</strong>; we then stop sending events linked to your account.</p>

      <h2>Who sees your data</h2>
      <p>Only members of a tree see that tree. Photos are served only to members of the tree they belong to. We share data only with the service providers that run Lineage for us, under contracts that limit how they use it:</p>
      <ul>
        <li><strong>Cloudflare</strong>: hosting, database and photo storage.</li>
        <li><strong>Google Firebase Authentication</strong>: sign-in. Firebase holds your credentials; we never see your password.</li>
        <li><strong>PostHog</strong>: usage statistics, as above.</li>
      </ul>
      <p>These providers may process data in Canada, the United States and other countries. We may also disclose data if the law requires it.</p>

      <h2>Children</h2>
      <p>You must be at least 13 to have a Lineage account. Family trees may include profiles of children added by their relatives; those profiles are visible only to the tree’s members. A parent or guardian can ask us to correct or remove a child’s profile at the contact below.</p>

      <h2>Your rights</h2>
      <ul>
        <li><strong>Access and export:</strong> owners and editors can export a tree (JSON and photos) at any time, and you can ask us for a copy of your account data.</li>
        <li><strong>Correction:</strong> edit your name in Settings, and tree details in the tree.</li>
        <li><strong>Deletion:</strong> delete your account in Settings; see <Link to="/delete-account">how deletion works</Link>.</li>
        <li><strong>Withdraw consent:</strong> turn off usage statistics in Settings at any time.</li>
      </ul>
      <p>You can also complain to the Office of the Privacy Commissioner of Canada, or to the data protection authority where you live.</p>

      <h2>Legal basis (EEA, UK and Switzerland)</h2>
      <p>We process account and tree data to provide the service you asked for (contract), and usage statistics only with your consent.</p>

      <h2>Changes</h2>
      <p>If we change this policy in a way that matters, we’ll ask you to agree again the next time you open Lineage.</p>

      <h2>Contact</h2>
      <p>Vir Ventures Inc, Ontario, Canada. Questions, access, correction or deletion requests: <a href="mailto:hi@virventures.io">hi@virventures.io</a>.</p>
      <p><Link to="/terms">Terms of Service</Link></p>
    </Doc>
  );
}

export function Terms() {
  return (
    <Doc title="Terms of Service">
      <p className="muted">Effective {EFFECTIVE}</p>
      <p>These terms are an agreement between you and Vir Ventures Inc (“we”, “us”), the company in Ontario, Canada that runs Lineage on the web at mylineage.app and in the iOS and Android apps. By creating an account or using Lineage you agree to these terms and to our <Link to="/privacy">Privacy Policy</Link>.</p>

      <h2>1. What Lineage is</h2>
      <p>Lineage lets you build a private family tree and invite relatives to see or edit it. It is free, has no ads, and may change or gain features over time.</p>

      <h2>2. Who can use it</h2>
      <p>You must be at least 13 years old to create an account. If you are under the age of majority where you live, you need a parent’s or guardian’s permission. You are responsible for keeping your sign-in secure and for what happens in your account.</p>

      <h2>3. Your content and other people’s information</h2>
      <ul>
        <li>You keep ownership of the names, dates, photos, stories and other content you add. You give us permission to store, copy and display it only as needed to run Lineage for you and the members of your tree.</li>
        <li>Trees hold information about other people, including living relatives and children. Only add information you have a right to share, respect a relative who asks to be removed or changed, and don’t add anything you know is false.</li>
        <li>Only upload photos you took or have permission to use.</li>
        <li>Content you add to a tree stays part of that family record after you leave the tree or delete your account, unless an owner or editor removes it.</li>
      </ul>

      <h2>4. Trees, roles and invites</h2>
      <p>The person who creates a tree is its owner and decides who is invited and what each member can do (owner, editor or viewer). Anyone with an invite link can join with the role it grants, so share links only with family you trust. Owners can remove members and delete a tree.</p>

      <h2>5. Acceptable use</h2>
      <p>Don’t use Lineage to harass, threaten or expose anyone; to share content that is illegal, sexual involving minors, hateful or that violates someone’s privacy or rights; to send spam; or to break, overload, probe or copy the service. We may remove content or suspend accounts that break these rules.</p>

      <h2>6. Privacy and usage statistics</h2>
      <p>Our <Link to="/privacy">Privacy Policy</Link> explains what we store and the usage statistics we collect, and how to turn them off.</p>

      <h2>7. Availability and changes</h2>
      <p>We work to keep Lineage running and your data safe, but we can’t promise it will always be available or error-free. Export your trees from time to time for your own records. We may change or discontinue features, and we’ll give reasonable notice before shutting Lineage down so you can export your data.</p>

      <h2>8. Disclaimers</h2>
      <p>Lineage is provided “as is” and “as available”. To the extent the law allows, we make no warranties about it, including fitness for a particular purpose. Relationship labels and family details are only as accurate as what members enter; Lineage is not a source of genealogical or legal proof.</p>

      <h2>9. Limitation of liability</h2>
      <p>To the extent the law allows, we are not liable for indirect, incidental, special or consequential damages, or for lost data or content, arising from your use of Lineage, and our total liability for any claim is limited to CAD $100. Nothing in these terms limits rights you have under consumer protection law that can’t be waived.</p>

      <h2>10. Ending your use</h2>
      <p>You can stop using Lineage and delete your account at any time in Settings. We may suspend or close an account that breaks these terms.</p>

      <h2>11. Changes to these terms</h2>
      <p>If we change these terms in a way that matters, we’ll ask you to agree again the next time you open Lineage. If you don’t agree, you can export your data and delete your account.</p>

      <h2>12. Governing law</h2>
      <p>These terms are governed by the laws of the Province of Ontario and the federal laws of Canada that apply there. Disputes go to the courts of Ontario, unless the law where you live gives you the right to bring them in your local courts.</p>

      <h2>13. Contact</h2>
      <p>Vir Ventures Inc, Ontario, Canada: <a href="mailto:hi@virventures.io">hi@virventures.io</a>.</p>
    </Doc>
  );
}

export function DeleteAccountInfo() {
  return (
    <Doc title="Delete your account">
      <p>You can delete your Lineage account from inside the app: <strong>Settings → Delete my account</strong>.</p>
      <ol>
        <li>If you own a tree, transfer ownership to another member or delete the tree first.</li>
        <li>Confirm by typing your email address, and re-enter your sign-in if asked.</li>
        <li>Your account, sign-in and tree memberships are removed. For Sign in with Apple, your Apple token is revoked.</li>
      </ol>
      <p>People profiles you added stay in the family trees, because they belong to the family record, but they are no longer linked to your account.</p>
      <p><Link to="/settings">Open Settings</Link></p>
    </Doc>
  );
}
